import { Col, Form, Input, Row, Select } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { tankApi } from '../../../../../apis'
import { ActionEnum, AlertMessageEnum } from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
  NumberFormat,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { TankEntity } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadFuels,
  loadProfile,
  loadPumps,
  setAction,
  setActionProcessing,
  setAlert,
  setSelectedTank,
  setTanks,
} from '../../../../../stores/actions'
import { TankRemainVolumeComponent } from './TankRemainVolumeComponent'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

type InputTank = {
  name: string
  volume: number
  remain?: number
  currentPrice: number
  fuelId: number
  stationId: number
}

export const ManageTankModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { selectedTank, tanks } = useSelector((state: RootState) => state.tank)
  const { language } = useSelector((state: RootState) => state.language)
  const { isPumpsLoaded } = useSelector((state: RootState) => state.pump)
  const { management } = useSelector((state: RootState) => state.common)
  const { stations, isStationsLoaded } = useSelector(
    (state: RootState) => state.station
  )
  const { fuels, isFuelsLoaded } = useSelector((state: RootState) => state.fuel)
  const readOnlyMode = React.useMemo(
    () => [ActionEnum.VIEW, ActionEnum.DELETE].includes(management.action),
    [management]
  )

  const [maxPrice, setMaxPrice] = React.useState(
    selectedTank &&
      fuels.filter((item) => item.id === selectedTank.fuel.id).length
      ? fuels.filter((item) => item.id === selectedTank.fuel.id)[0].price
      : -1
  )

  React.useEffect(() => {
    dispatch(loadFuels())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    setMaxPrice(
      selectedTank &&
        fuels.filter((item) => item.id === selectedTank.fuel.id).length
        ? fuels.filter((item) => item.id === selectedTank.fuel.id)[0].price
        : -1
    )
  }, [fuels, selectedTank])

  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const replaceTank = (tank: TankEntity) => {
    const index = tanks.map((item) => item.id).indexOf(tank.id)
    if (index > -1) {
      dispatch(
        setTanks([...tanks.slice(0, index), tank, ...tanks.slice(index + 1)])
      )
    }
  }

  const onFinish = async (value: InputTank) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    try {
      switch (management.action) {
        case ActionEnum.VIEW:
          dispatch(setAction(ActionEnum.UPDATE))
          break
        case ActionEnum.UPDATE:
          if (selectedTank) {
            try {
              const respUpdate = await tankApi.updateTank(
                selectedTank.id,
                value
              )
              replaceTank(respUpdate.data)
              if (selectedTank.name !== value.name && isPumpsLoaded) {
                dispatch(loadPumps())
              }
              dispatch(setSelectedTank())
              dispatch(
                setAlert({
                  key: 'tank.update.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } catch (e) {
              if (e && e.response && e.response.status === 401) {
                dispatch(loadProfile())
                const respUpdate = await tankApi.updateTank(
                  selectedTank.id,
                  value
                )
                replaceTank(respUpdate.data)
                if (selectedTank.name !== value.name && isPumpsLoaded) {
                  dispatch(loadPumps())
                }
                dispatch(setSelectedTank())
                dispatch(
                  setAlert({
                    key: 'tank.update.success',
                    type: AlertMessageEnum.SUCCESS,
                  })
                )
              } else if (e && e.response && e.response.status === 409) {
                dispatch(
                  setAlert({
                    key: 'tank.update.fail.duplicate',
                    type: AlertMessageEnum.ERROR,
                  })
                )
              } else {
                dispatch(
                  setAlert({
                    key: 'tank.update.fail.internal',
                    type: AlertMessageEnum.ERROR,
                  })
                )
              }
            }
          }
          break
        case ActionEnum.CREATE:
          try {
            const respCreate = await tankApi.createTank(value)
            dispatch(setTanks([respCreate.data, ...tanks]))
            dispatch(setSelectedTank())
            dispatch(
              setAlert({
                key: 'tank.create.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e && e.response && e.response.status === 401) {
              dispatch(loadProfile())
              const respCreate = await tankApi.createTank(value)
              dispatch(setTanks([respCreate.data, ...tanks]))
              dispatch(setSelectedTank())
              dispatch(
                setAlert({
                  key: 'tank.create.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else if (e && e.response && e.response.status === 409) {
              dispatch(
                setAlert({
                  key: 'tank.create.fail.duplicate',
                  type: AlertMessageEnum.ERROR,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'tank.create.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
          break
        case ActionEnum.DELETE:
          try {
            await tankApi.deleteTank(selectedTank?.id || 0)
            dispatch(
              setTanks(tanks.filter((item) => item.id !== selectedTank?.id))
            )
            dispatch(setSelectedTank())
            dispatch(
              setAlert({
                key: 'tank.delete.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e.response && e.response.status && e.response.status === 401) {
              dispatch(loadProfile())
              await tankApi.deleteTank(selectedTank?.id || 0)
              dispatch(
                setTanks(tanks.filter((item) => item.id !== selectedTank?.id))
              )
              dispatch(setSelectedTank())
              dispatch(
                setAlert({
                  key: 'tank.delete.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key:
                    e.response && e.response.status && e.response.status === 400
                      ? 'common.delete.fail.fkConstraint'
                      : 'tank.delete.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
          break
        default:
          break
      }
    } catch (e) {}
    dispatch(setActionProcessing(false))
  }

  const body = (
    <Form onFinish={onFinish} form={form}>
      {(!management.alertMessage ||
        management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
        <>
          {readOnlyMode && selectedTank && (
            <Row gutter={24}>
              <Col span={isDesktopScreen ? 16 : 24}>
                <>
                  <StyledFormItem
                    label={
                      <StyledLabel>
                        {translate('tank.id', language)}
                      </StyledLabel>
                    }
                  >
                    {selectedTank.id}
                  </StyledFormItem>
                  <StyledFormItem
                    label={
                      <StyledLabel>
                        {translate('tank.name', language)}
                      </StyledLabel>
                    }
                  >
                    {selectedTank.name}
                  </StyledFormItem>
                  <StyledFormItem
                    label={
                      <StyledLabel>
                        {translate('tank.volume', language)}
                      </StyledLabel>
                    }
                  >
                    <NumberFormat
                      value={selectedTank.volume}
                      suffixKey="common.unitType"
                    />
                  </StyledFormItem>
                  <StyledFormItem
                    label={
                      <StyledLabel>
                        {translate('tank.remain', language)}
                      </StyledLabel>
                    }
                  >
                    <NumberFormat
                      value={selectedTank.remain}
                      suffixKey="common.unitType"
                    />
                  </StyledFormItem>
                  <StyledFormItem
                    label={
                      <StyledLabel>
                        {translate('tank.currentPrice', language)}
                      </StyledLabel>
                    }
                  >
                    <NumberFormat
                      value={selectedTank.currentPrice}
                      suffixKey="common.moneyType"
                    />
                  </StyledFormItem>
                  <StyledFormItem
                    label={
                      <StyledLabel>
                        {translate('tank.station', language)}
                      </StyledLabel>
                    }
                  >
                    {selectedTank.station.name}
                  </StyledFormItem>
                  <StyledFormItem
                    label={
                      <StyledLabel>
                        {translate('tank.fuel', language)}
                      </StyledLabel>
                    }
                  >
                    {selectedTank.fuel.name}
                  </StyledFormItem>
                </>
              </Col>
              {isDesktopScreen && (
                <Col span={8}>
                  <TankRemainVolumeComponent
                    total={selectedTank.volume}
                    remain={selectedTank.remain}
                  />
                </Col>
              )}
            </Row>
          )}
          {selectedTank && management.action === ActionEnum.UPDATE && (
            <StyledFormItem
              label={
                <StyledLabel>{translate('tank.id', language)}</StyledLabel>
              }
            >
              {selectedTank.id}
            </StyledFormItem>
          )}
          {!readOnlyMode && (
            <StyledFormItem
              name="name"
              label={
                <StyledLabel>{translate('tank.name', language)}</StyledLabel>
              }
              initialValue={selectedTank?.name}
              rules={[
                {
                  required: true,
                  message: '',
                },
                () => ({
                  validator(_, value) {
                    if (!value || !value.trim().length) {
                      return Promise.reject(
                        new Error(translate('tank.invalid.name', language))
                      )
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input
                placeholder={translate('tank.name', language)}
                allowClear
              />
            </StyledFormItem>
          )}
          {selectedTank && management.action === ActionEnum.UPDATE && (
            <>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('tank.volume', language)}
                  </StyledLabel>
                }
              >
                {selectedTank.volume}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('tank.remain', language)}
                  </StyledLabel>
                }
              >
                {selectedTank?.remain}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('tank.station', language)}
                  </StyledLabel>
                }
              >
                {selectedTank.station.name}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>{translate('tank.fuel', language)}</StyledLabel>
                }
              >
                {selectedTank.fuel.name}
              </StyledFormItem>
            </>
          )}
          {!readOnlyMode && (
            <StyledFormItem
              name="currentPrice"
              label={
                <StyledLabel>
                  {translate('tank.currentPrice', language)}
                </StyledLabel>
              }
              initialValue={selectedTank?.currentPrice}
              rules={[
                {
                  required: true,
                  message: '',
                },
                () => ({
                  validator(_, value) {
                    if (
                      !value ||
                      value < 1 ||
                      (maxPrice !== -1 && maxPrice < value)
                    ) {
                      return Promise.reject(
                        new Error(
                          `${translate('tank.invalid.currentPrice', language)}${
                            maxPrice > -1 ? ` (${maxPrice})` : ''
                          }`
                        )
                      )
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input
                type="number"
                placeholder={translate('tank.currentPrice', language)}
                allowClear
              />
            </StyledFormItem>
          )}
          {management.action === ActionEnum.CREATE && (
            <>
              <StyledFormItem
                name="volume"
                label={
                  <StyledLabel>
                    {translate('tank.volume', language)}
                  </StyledLabel>
                }
                initialValue={selectedTank?.volume}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || value < 1) {
                        return Promise.reject(
                          new Error(translate('tank.invalid.volume', language))
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  type="number"
                  placeholder={translate('tank.volume', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="fuelId"
                label={
                  <StyledLabel>{translate('tank.fuel', language)}</StyledLabel>
                }
                initialValue={selectedTank?.fuel.id}
                rules={[
                  {
                    required: true,
                    message: translate('tank.required.fuel', language),
                  },
                ]}
              >
                <Select
                  placeholder={translate('tank.fuel', language)}
                  style={{ width: '100%' }}
                  loading={!isFuelsLoaded}
                  disabled={!isFuelsLoaded}
                  onChange={(e) =>
                    setMaxPrice(
                      fuels.filter((item) => item.id === e).length
                        ? fuels.filter((item) => item.id === e)[0].price
                        : -1
                    )
                  }
                >
                  {fuels.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {`${item.name}`}
                    </Select.Option>
                  ))}
                </Select>
              </StyledFormItem>
              <StyledFormItem
                name="stationId"
                label={
                  <StyledLabel>
                    {translate('tank.station', language)}
                  </StyledLabel>
                }
                initialValue={selectedTank?.station.id}
                rules={[
                  {
                    required: true,
                    message: translate('tank.required.station', language),
                  },
                ]}
              >
                <Select
                  placeholder={translate('tank.station', language)}
                  style={{ width: '100%' }}
                  loading={!isStationsLoaded}
                  disabled={!isStationsLoaded}
                >
                  {stations.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {`${item.name}`}
                    </Select.Option>
                  ))}
                </Select>
              </StyledFormItem>
            </>
          )}
        </>
      )}
      <ManageModalFooterComponent
        hasDefaultButtons
        onDeleteConfirm={() => form.submit()}
      />
    </Form>
  )

  return (
    <ManageModalComponent
      body={body}
      width={isDesktopScreen ? '40%' : '100%'}
    />
  )
}
